import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type Organization = Database['public']['Tables']['organizations']['Row']
type OrganizationUpdate =
  Database['public']['Tables']['organizations']['Update']

async function updateOrganization(
  orgId: string,
  input: Pick<OrganizationUpdate, 'name' | 'brand_identity'>
) {
  if (!orgId || orgId.trim() === '') {
    throw new Error(
      'Invalid orgId: cannot update organization without valid organization ID'
    )
  }

  const { data, error } = await supabase
    .from('organizations')
    .update({
      name: input.name,
      brand_identity: input.brand_identity,
    })
    .eq('id', orgId)
    .select('*')
    .single()

  if (error) {
    throw new Error(`Failed to update organization: ${error.message}`, {
      cause: error,
    })
  }

  if (!data) {
    throw new Error('Organization not found')
  }

  return data
}

export const organization = {
  findOne: (orgId: string) =>
    queryOptions({
      queryKey: ['organization', orgId],
      queryFn: async () => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch organization without valid organization ID'
          )
        }

        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', orgId)
          .single()

        if (error) {
          throw new Error(`Failed to fetch organization: ${error.message}`, {
            cause: error,
          })
        }

        if (!data) {
          throw new Error('Organization not found')
        }

        return data
      },
    }),

  findMany: () =>
    queryOptions({
      queryKey: ['organizations', 'user'],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch user organizations: ${error.message}`,
            {
              cause: error,
            }
          )
        }

        return data
      },
    }),

  update: updateOrganization,
}
